/*
 * This file is part of ORY Editor.
 *
 * ORY Editor is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * ORY Editor is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with ORY Editor.  If not, see <http://www.gnu.org/licenses/>.
 *
 * @license LGPL-3.0
 * @copyright 2016-2018 Aeneas Rekkas
 * @author Aeneas Rekkas <aeneas+oss@aeneas.io>
 *
 */
var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __spreadArrays = this && this.__spreadArrays || function () {
  for (var s = 0, i = 0, il = arguments.length; i < il; i++) {
    s += arguments[i].length;
  }

  for (var r = Array(s), k = 0, i = 0; i < il; i++) {
    for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++) {
      r[k] = a[j];
    }
  }

  return r;
};

import forEach from 'ramda/src/forEach';
import { NativeTypes } from 'react-dnd-html5-backend-cjs';
import { v4 } from 'uuid';
import { actions, Actions } from './actions';
import DragDropProvider from './components/DragDropProvider';
import Editable from './components/Editable';
import { isProduction } from './const';
import lazyLoad from './helper/lazyLoad';
import sanitizeInitialChildren from './helper/sanitizeInitialChildren';
import { shouldPureComponentUpdate } from './helper/shouldComponentUpdate';
import { reducer } from './reducer';
import { connect, ReduxContext, ReduxProvider } from './reduxConnect';
import { selectors, Selectors } from './selector';
import i18n from './service/i18n';
import PluginService from './service/plugin';
import { ContentPlugin, LayoutPlugin, Plugin } from './service/plugin/classes';
import pluginDefault from './service/plugin/default';
import createStore from './store';
export { shouldPureComponentUpdate };
export { Plugin, Actions, Selectors, i18n, sanitizeInitialChildren, PluginService, ContentPlugin, LayoutPlugin, Editable, Editor, reducer, lazyLoad, ReduxProvider, DragDropProvider, connect, ReduxContext };

var initialState = function initialState() {
  return {
    reactPage: {
      editables: {
        past: [],
        present: [],
        future: []
      }
    }
  };
};

var nativeTypes = function nativeTypes(editor) {
  return editor.plugins.hasNativePlugin() ? [NativeTypes.URL, NativeTypes.FILE, NativeTypes.TEXT] : [];
};

var update = function update(editor) {
  return function (editable) {
    var state = editor.plugins.unserialize(editable);
    actions(editor.store.dispatch).editable.update(__assign(__assign({}, state), {
      config: {
        plugins: editor.plugins,
        whitelist: __spreadArrays(editor.plugins.getRegisteredNames(), nativeTypes(editor))
      }
    }));
  };
};
/**
 * Editor is the core interface for dealing with the editor.
 */


var Editor =
/** @class */
function () {
  function Editor(_a) {
    var _this = this;

    var _b = _a === void 0 ? {} : _a,
        plugins = _b.plugins,
        _c = _b.middleware,
        middleware = _c === void 0 ? [] : _c,
        _d = _b.editables,
        editables = _d === void 0 ? [] : _d,
        _e = _b.defaultPlugin,
        defaultPlugin = _e === void 0 ? pluginDefault : _e,
        store = _b.store;

    this.query = {};

    this.refreshEditables = function () {
      forEach(function (editable) {
        if (!isProduction) {
          // tslint:disable-next-line:no-console
          console.log(_this.plugins.serialize(editable));
        } // tslint:disable-next-line:no-any


        _this.trigger.editable.update(_this.plugins.serialize(editable));
      }, _this.store.getState().reactPage.editables.present);
    };
    /**
     * @deprecated in order to reduce the api surface, we will remove this method in the future
     */


    this.setLayoutPlugins = function (plugins) {
      if (plugins === void 0) {
        plugins = [];
      }

      console.warn('in order to reduce the api surface, we will remove setLayoutPlugins in the future');

      _this.plugins.setLayoutPlugins(plugins);

      _this.refreshEditables();
    };
    /**
     * @deprecated in order to reduce the api surface, we will remove this method in the future
     */


    this.addLayoutPlugin = function (config) {
      console.warn('in order to reduce the api surface, we will remove addLayoutPlugin in the future');

      _this.plugins.addLayoutPlugin(config);

      _this.refreshEditables();
    };
    /**
     * @deprecated in order to reduce the api surface, we will remove this method in the future
     */


    this.removeLayoutPlugin = function (name) {
      console.warn('in order to reduce the api surface, we will remove removeLayoutPlugin in the future');

      _this.plugins.removeLayoutPlugin(name);

      _this.refreshEditables();
    };
    /**
     * @deprecated in order to reduce the api surface, we will remove this method in the future
     */


    this.setContentPlugins = function (plugins) {
      if (plugins === void 0) {
        plugins = [];
      }

      console.warn('in order to reduce the api surface, we will remove setContentPlugins in the future');

      _this.plugins.setContentPlugins(plugins);

      _this.refreshEditables();
    };
    /**
     * @deprecated in order to reduce the api surface, we will remove this method in the future
     */


    this.addContentPlugin = function (config) {
      console.warn('in order to reduce the api surface, we will remove addContentPlugin in the future');

      _this.plugins.addContentPlugin(config);

      _this.refreshEditables();
    };
    /**
     * @deprecated in order to reduce the api surface, we will remove this method in the future
     */


    this.removeContentPlugin = function (name) {
      console.warn('in order to reduce the api surface, we will remove removeContentPlugin in the future');

      _this.plugins.removeContentPlugin(name);

      _this.refreshEditables();
    };

    this.store = store || createStore(initialState(), middleware);
    this.plugins = new PluginService(plugins);
    this.middleware = middleware;
    this.trigger = actions(this.store.dispatch);
    this.query = selectors(this.store);
    this.defaultPlugin = defaultPlugin; // tslint:disable-next-line:no-any

    this.trigger.editable.add = update(this); // tslint:disable-next-line:no-any

    this.trigger.editable.update = update(this);
    editables.forEach(this.trigger.editable.add);
  }

  return Editor;
}();

export var createEmptyState = function createEmptyState() {
  return {
    id: v4(),
    cells: []
  };
};
export default Editor;